import { useState, useEffect } from 'react';

import dynamic from 'next/dynamic';
import { throttle } from 'lodash-es';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import { createGlobalStyle } from 'styled-components';

import Tooltip from '@components/ui/Tooltip/next';
import SeturExtra from '@assets/images/seturExtra.png';
import {
  HeaderCampaignItemViewModel,
  HeaderSubCategoryItemViewModel,
} from '@api/index';
import { addMenuClickEventsToGTM } from '@utils/gtm/menu-click-events-gtm';
import { productImpression } from '@utils/newDataLayer/productImpression';
import { useSkipEffectOnMount } from '@hooks/lifecycle';
import palette from '@utils/theme/palette';

import { HeaderItem, HeaderRegularItem, HeaderText, LabelText } from './styled';

const MegaMenu = dynamic(() => import('../MegaMenu/MegaMenu'));
const HeaderTooltipStyles = createGlobalStyle`
& .rc-tooltip-arrow {
  border-color: #ffffff transparent #E2231B transparent !important;
  top: -5px !important;
}

`;
interface HeaderMenuItemProps {
  label?: string;
  href?: string;
  items: HeaderSubCategoryItemViewModel[];
  campaigns: Array<HeaderCampaignItemViewModel>;
  isTransparentHeader?: boolean;
  setIsLoading: (val: boolean) => void;
  isLoading?: boolean;
}
const HeaderMenuItem = ({
  label,
  href,
  items,
  campaigns,
  isTransparentHeader,
  setIsLoading,
}: HeaderMenuItemProps) => {
  const [visibleMegaMenu, setVisibleMegaMenu] = useState(false);
  const query = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const showMenu = () => setVisibleMegaMenu(true);
  const hideMenu = () => setVisibleMegaMenu(false);
  useEffect(() => {
    window.addEventListener('scroll', throttle(hideMenu, 200), {
      passive: true,
    });
  }, []);

  useSkipEffectOnMount(() => {
    hideMenu();
  }, [query]);

  if (items.length === 0)
    return (
      <HeaderRegularItem
        isMargin={label === 'Setur Extra'}
        isTransparentHeader={isTransparentHeader}
      >
        <HeaderText
          onClick={() => {
            if (pathname !== href) setIsLoading(true);
            label && addMenuClickEventsToGTM(label);
            if (label?.includes('Oteller'))
              productImpression({
                item_list_location: 'Category Header',
                item_list_id: label,
                type: 'hotel',
              });
            if (href) {
              router.push(href);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (pathname !== href) setIsLoading(true);
              label && addMenuClickEventsToGTM(label);
              if (label?.includes('Oteller'))
                productImpression({
                  item_list_location: 'Category Header',
                  item_list_id: label,
                  type: 'hotel',
                });
              if (href) {
                router.push(href);
              }
            }
          }}
          tabIndex={0}
          title={label}
        >
          {typeof label === 'string' && label === 'Setur Extra' ? (
            <Image
              src={SeturExtra}
              width={92}
              height={25}
              alt='Setur Extra'
              priority
            />
          ) : (
            label
          )}
        </HeaderText>
      </HeaderRegularItem>
    );

  return (
    <>
      <HeaderTooltipStyles />
      <HeaderItem
        onMouseEnter={showMenu}
        onMouseLeave={hideMenu}
        isTransparentHeader={isTransparentHeader}
        isMegaMenuVisible={visibleMegaMenu}
      >
        <Tooltip
          trigger='hover'
          overlayInnerStyle={{
            lineHeight: 1.43,
            textAlign: 'left',
            width: 'auto',
            height: '100%',
            backgroundColor: palette.white,
            borderRadius: '8px',
            padding: '0px',
            position: 'relative',
            top: '-5px',
            right: '10px',
          }}
          placement='bottomLeft'
          title={
            <MegaMenu
              setIsLoading={setIsLoading}
              menuTitle={label}
              items={items}
              visible={visibleMegaMenu && items.length > 0}
              campaigns={campaigns}
            />
          }
          arrowColor={palette.white}
          getPopupContainer={() => document.body} // Tooltip'i body içine taşı
        >
          <Link
            href={href || ''}
            prefetch={false}
            onClick={() => {
              if (label === 'Kıbrıs Otelleri')
                productImpression({
                  item_list_location: 'Category Header',
                  item_list_id: label,
                  type: 'hotel',
                });
            }}
          >
            <LabelText>{label}</LabelText>
          </Link>
        </Tooltip>
      </HeaderItem>
    </>
  );
};

export default HeaderMenuItem;
